<template>
  <v-container fluid>
    <v-row justify="center" align="center" v-if="loading">
      <div class="text-center">
        <div>
          <v-progress-circular
            class="my-4"
            :size="50"
            width="5"
            color="primary"
            indeterminate
          ></v-progress-circular>
        </div>
        <p class="display-1">{{ $t("Loading") }}...</p>
      </div>
    </v-row>
    <v-row dense v-if="!loading">
      <v-col md="6" sm="12">
        <v-card>
          <v-card-title>
            <v-icon large left> mdi-cogs </v-icon>
            <span class="title font-weight-light">{{ $t("General") }}</span>
          </v-card-title>
          <v-card-text>
            <v-switch
              v-model="parameter.DashboardCacheEnabled"
              @change="cacheToggleChanged"
              color="green"
              :label="$t('ParamDashboardCache')"
            ></v-switch>
            <v-text-field
              :label="$t('ParamWebsiteURL')"
              v-model="parameter.WebsiteURL"
            ></v-text-field>
            <v-text-field
              :label="$t('ParamWebApiURL')"
              v-model="parameter.WebApiURL"
            ></v-text-field>
            <v-text-field
              :label="$t('ParamAutorecognitionApiURL')"
              v-model="parameter.AutorecognitionApiURL"
            ></v-text-field>
            <v-text-field
              :label="$t('JobsExecutionTimeAlert')"
              v-model="parameter.JobsExecutionTimeAlert"
            ></v-text-field>
            <v-text-field
              :label="$t('JobsExecutionTimeDataManagement')"
              v-model="parameter.JobsExecutionTimeDataManagement"
            ></v-text-field>
            <v-text-field
              :label="$t('JobsExecutionTimeDelegations')"
              v-model="parameter.JobsExecutionTimeDelegations"
            ></v-text-field>
            <v-text-field
              :label="$t('JobsExecutionTimeDocumentLifeCycle')"
              v-model="parameter.JobsExecutionTimeDocumentLifeCycle"
            ></v-text-field>
            <v-text-field
              :label="$t('JobsExecutionTimeLicence')"
              v-model="parameter.JobsExecutionTimeLicence"
            ></v-text-field>
            <v-text-field
              :label="$t('JobsExecutionTimeConnectedServices')"
              v-model="parameter.JobsExecutionTimeConnectedServices"
            ></v-text-field>
            <v-textarea
              :label="$t('ParamSupportedFileFormats')"
              v-model="parameter.SupportedFileFormats"
            >
              <template v-slot:append>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on"> mdi-help-circle-outline </v-icon>
                  </template>
                  {{ $t("ParamSupportedFileFormatsInfo") }}
                </v-tooltip>
              </template>
            </v-textarea>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col md="6" sm="12">
        <v-card>
          <v-card-title>
            <v-icon large left> mdi-server-security </v-icon>
            <v-switch
              v-model="parameter.ActiveDirectoryAuthEnabled"
              color="green"
              :label="$t('ParamActiveDirectory')"
            ></v-switch>
          </v-card-title>
          <v-card-text v-if="parameter.ActiveDirectoryAuthEnabled">
            <v-switch
              v-model="parameter.AzureUseActiveDirectory"
              color="green"
              :label="$t('UseAzure')"
            ></v-switch>
            <v-select
              v-if="parameter.AzureUseActiveDirectory"
              :label="$t('EntraIdDefaultOrganization')"
              v-model="parameter.MicrosoftEntraIDDefaultOrganizationId"
              :items="organizations"
              item-text="text"
              item-value="id"
            ></v-select>
            <v-text-field
              v-if="parameter.AzureUseActiveDirectory"
              :label="$t('ClientID')"
              v-model="parameter.MicrosoftEntraClientId"
            ></v-text-field>
            <v-text-field
              v-if="parameter.AzureUseActiveDirectory"
              :label="$t('ClientSecret')"
              v-model="parameter.MicrosoftEntraClientSecret"
            ></v-text-field>
            <v-text-field
              v-if="!parameter.AzureUseActiveDirectory"
              v-model="parameter.ActiveDirectoryServerPath"
              :label="$t('Server')"
            >
              <template v-slot:append v-if="!parameter.AzureUseActiveDirectory">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on"> mdi-help-circle-outline </v-icon>
                  </template>
                  LDAP://srv-ecm.adserver.local
                </v-tooltip>
              </template>
            </v-text-field>
            <v-text-field
              v-if="!parameter.AzureUseActiveDirectory"
              :label="$t('ParamActiveDirectoryDomain')"
              v-model="parameter.ActiveDirectoryDomain"
            >
              <template v-slot:append>
                <v-tooltip bottom>
                  <template v-slot:activator="{ on }">
                    <v-icon v-on="on"> mdi-help-circle-outline </v-icon>
                  </template>
                  adserver.local
                </v-tooltip>
              </template>
            </v-text-field>
            <v-text-field
              v-if="!parameter.AzureUseActiveDirectory"
              :label="$t('ParamActiveDirectoryAllowedUserGroups')"
              v-model="parameter.ActiveDirectoryAllowedUserGroups"
            ></v-text-field>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-row dense v-if="!loading">
      <v-col md="6" sm="12">
        <v-card>
          <v-card-title>
            <v-icon large left> mdi-security </v-icon>
            <v-switch
              v-model="parameter.SAMLEnabled"
              color="green"
              :label="$t('SAML')"
            ></v-switch>
          </v-card-title>
          <v-card-text v-if="parameter.SAMLEnabled">
            <v-text-field
              :label="$t('ParamSAMLEndpoint')"
              v-model="parameter.SAMLEndpoint"
            ></v-text-field>
            <v-textarea
              v-model="parameter.SAMLCertificate"
              :label="$t('ParamSAMLCertificate')"
            ></v-textarea>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col md="6" sm="12" v-show="false">
        <v-card>
          <v-card-title>
            <v-icon large left> mdi-draw </v-icon>
            <v-switch
              v-model="parameter.SwissIDEnabled"
              color="green"
              :label="$t('QualifiedElectronicSignature')"
            ></v-switch>
          </v-card-title>
          <v-card-text v-if="parameter.SwissIDEnabled">
            <v-text-field
              :label="$t('Login')"
              v-model="parameter.SwissIDLogin"
            ></v-text-field>
            <v-text-field
              :label="$t('Password')"
              v-model="parameter.SwissIDPassword"
            ></v-text-field>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col>
        <v-card>
          <v-card-title>
            <v-icon large left> mdi-draw </v-icon>
            <span class="title font-weight-light">{{ $t("QuoVadis V2") }}</span>
          </v-card-title>
          <v-card-text>
            <v-text-field
              :label="$t('ClientID')"
              v-model="parameter.ClientID"
            ></v-text-field>
            <v-text-field
              :label="$t('CredentialID')"
              v-model="parameter.CredentialID"
            ></v-text-field>
            <v-text-field
              :label="$t('ClientSecret')"
              v-model="parameter.ClientSecret"
              :append-icon="showSecret ? 'mdi-eye' : 'mdi-eye-off'"
              @click:append="showSecret = !showSecret"
              :type="showSecret ? 'text' : 'password'"
            ></v-text-field>
            <v-text-field
              :label="$t('UserID')"
              v-model="parameter.UserID"
            ></v-text-field>
            <v-text-field
              :label="$t('ApiURL')"
              v-model="parameter.QuoVadisV2ApiURL"
            ></v-text-field>
            <v-row class="pl-3">
              <v-btn small color="primary" @click="generateSAD">
                {{ $t("GenerateSAD") }}
                <v-icon>mdi-account-credit-card-outline</v-icon>
              </v-btn>
              <span class="title font-weight-light"
                >{{ $t("ExpiresOn") }}: {{ expirationSAD }}</span
              >
              <h1></h1>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-fab-transition>
      <v-tooltip left>
        <template v-slot:activator="{ on }">
          <v-btn
            color="primary"
            v-on="on"
            dark
            absolute
            bottom
            right
            fab
            :loading="loadingSave"
            class="mb-10"
            @click="save"
          >
            <v-icon large>mdi-content-save</v-icon>
          </v-btn>
        </template>
        <span>{{ $t("Save") }}</span>
      </v-tooltip>
    </v-fab-transition>
  </v-container>
</template>

<script>
import ParameterService from "@/services/ParameterService";
import OrganizationService from "@/services/OrganizationService";
import Parameter from "@/ecm/models/Parameter";
import Utils from "@/ecm/common/Utils";

export default {
  mounted() {
    this.getParameters();
    this.getOrganizations();
  },
  data() {
    return {
      loading: false,
      loadingSave: false,
      parameter: new Parameter(),
      cacheChanged: false,
      expirationSAD: "",
      showSecret: false,
      organizations: [],
    };
  },
  methods: {
    getOrganizations() {
      this.loading = true;
      OrganizationService.getList().then((data) => {
        this.organizations = data;
        this.organizations = this.organizations.filter((org) => org.id !== "1");
        this.loading = false;
      });
    },
    getParameters() {
      this.loading = true;
      ParameterService.getAll().then((data) => {
        this.parameter = new Parameter(data);
        this.getSADExpiration();
        this.loading = false;
      });
    },
    cacheToggleChanged() {
      this.cacheChanged = true;
    },
    generateSAD() {
      this.$confirm(this.$t("GenerateSADConfirmation"), {
        title: this.$t("GenerateSAD"),
        icon: "mdi-alert",
        color: "orange",
        buttonTrueText: this.$t("Accept"),
        buttonFalseText: this.$t("Cancel"),
      }).then((res) => {
        if (res == 1) {
          this.loadingSave = true;
          ParameterService.save(this.parameter.toDto()).then((data) => {
            ParameterService.generateSAD().then((data2) => {
              let message = "SAD " + this.$t("CreatedSuccessfully");
              Utils.toast.success(message);
              this.getSADExpiration();
              this.getParameters();
              this.loadingSave = false;
            });
          });
        }
      });
    },
    save() {
      this.loadingSave = true;
      ParameterService.save(this.parameter.toDto()).then((data) => {
        let message =
          this.$t("Parameters") + " " + this.$t("SavedSuccessfully");
        if (this.cacheChanged && this.parameter.DashboardCacheEnabled) {
          message += ". " + this.$t("RebootAPIToApplyCacheChanges");
        }
        Utils.toast.success(message);
        this.getParameters();
        this.loadingSave = false;
      });
    },
    getSADExpiration() {
      let userDateFormat = this.$store.getters["account/user"].MetricDateFormat;
      ParameterService.getSADExpiration().then((data) => {
        var expirationDate = new Date(data);
        var today = new Date();
        if (expirationDate > today) {
          this.expirationSAD = Utils.date.toUserFormat(
            expirationDate,
            userDateFormat
          );
        } else {
          this.expirationSAD = this.$t("Expired");
        }
      });
    },
  },
};
</script>
